import { ReactNode } from 'react';

import { NoDataProps } from '@/components/legacy-ui/NoData';
import { createClearFiltersButton } from '@/components/legacy-ui/utils/createClearFiltersButton';
import { Button } from '@/components/ui/button';

/** Standardized icon class for NoData components */
export const NO_DATA_ICON_CLASS = 'text-chariot-stroke';

interface FilteredEmptyParams {
  /** Entity name for title (e.g., "Assets", "Vulnerabilities") */
  entityName: string;
  /** Icon component */
  icon: ReactNode;
  /** Whether filters are currently active */
  hasActiveFilters: boolean;
  /** Callback to clear all filters */
  onClearFilters: () => void;
}

interface AllEmptyParams {
  /** Entity name for title */
  entityName: string;
  /** Icon component */
  icon: ReactNode;
  /** Message explaining how to populate data */
  description?: ReactNode;
  /** "Get Started" callback (omit to hide button) */
  onGetStarted?: () => void;
  /** Override default button label */
  actionLabel?: string;
  /** Optional data-tutorial attribute for the action button */
  dataTutorial?: string;
}

/**
 * Creates NoData config for filtered empty state.
 * Use when user has data but current filters return no results.
 *
 * @example
 * ```tsx
 * const noDataConfig = createFilteredEmptyConfig({
 *   entityName: 'assets',
 *   icon: <AssetIcon className={NO_DATA_ICON_CLASS} />,
 *   hasActiveFilters: true,
 *   onClearFilters: handleClearFilters,
 * });
 * ```
 */
export function createFilteredEmptyConfig({
  entityName,
  icon,
  hasActiveFilters,
  onClearFilters,
}: FilteredEmptyParams): NoDataProps {
  return {
    icon,
    title: `No ${entityName} match your current filter`,
    action: createClearFiltersButton({ hasActiveFilters, onClearFilters }),
  };
}

/**
 * Creates NoData config for all empty state.
 * Use when no data exists (not due to filtering).
 *
 * @example
 * ```tsx
 * const noDataConfig = createAllEmptyConfig({
 *   entityName: 'No assets impacted by this technology',
 *   icon: <AssetIcon className={NO_DATA_ICON_CLASS} />,
 * });
 * ```
 */
export function createAllEmptyConfig({
  entityName,
  icon,
  description,
  onGetStarted,
  actionLabel = 'Get Started',
  dataTutorial,
}: AllEmptyParams): NoDataProps {
  return {
    icon,
    title: entityName,
    description,
    action: onGetStarted ? (
      <Button onClick={onGetStarted} size="sm" data-tutorial={dataTutorial}>
        {actionLabel}
      </Button>
    ) : undefined,
  };
}
